import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { IPublicForm } from "../../../Forms/PublicForm/IPublicForm";
import { DisableUnitActionsContext } from "../Unit/DisableUnitActionsContext";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { LoadedAttachmentContext } from "../Unit/LoadedAttachmentContext";
import { ExtraPageActionAttachment } from "../../../Forms/Client/Client";
import { UnitCaption } from "../Unit/Internals/UnitCaption/UnitCaption";
import { Header } from "../Header/Header";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { SetUnitItemExtraInfo } from "../Unit/Types";

import { FormInFormContainer, HeaderWrapper } from "./AttachmentForm.styles";

export interface NavigationSettings {
    navigationAttachmentId?: string;
    navigationPath?: string;
    navigationQueryPath?: string;
    navigationHighlanderPath?: string;
    errorItemId?: string;
}

export interface AttachmentFormRendererProps {
    onRenderAttachmentForm: (publicForm: IPublicForm) => void;
    onRemoveAttachment: () => void;
    onClearAttachment?: () => void;
    draftId: string;
    ns: string;
    attachmentId: string;
    caption: string;
    description: string;
    navigationSettings: NavigationSettings;
    switchOnErrorsLogTooltip: (highlanderPath: string, errorId: string, attachmentId: string) => Promise<void>;
    switchOffErrorsLogTooltip: () => void;
    extraPageActions?: ExtraPageActionAttachment[];
    setUnitItemExtraInfo?: SetUnitItemExtraInfo;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[];
}

export function AttachmentFormRenderer(props: AttachmentFormRendererProps): JSX.Element {
    const containerRef = React.createRef<HTMLDivElement>();
    const publicFormRef = useRef<IPublicForm>();
    const [loaded, setLoaded] = useState<boolean>(false);

    const disableUnitActionsContext = useContext(DisableUnitActionsContext);
    const loadedFormContext = useContext(LoadedAttachmentContext);
    const { navigationAttachmentId, navigationHighlanderPath, errorItemId } = props.navigationSettings;

    useAsyncEffect(async () => {
        if (
            publicFormRef.current &&
            props.attachmentId === navigationAttachmentId &&
            navigationHighlanderPath &&
            loaded
        ) {
            if (errorItemId) {
                await props.switchOnErrorsLogTooltip(navigationHighlanderPath, errorItemId, props.attachmentId);
            } else {
                props.switchOffErrorsLogTooltip();
            }
        }
    }, [navigationAttachmentId, navigationHighlanderPath, errorItemId, loaded]);

    useEffect(() => {
        disableUnitActionsContext?.setDisableUnitActions?.({
            disableRemove: !loaded,
            disableCopy: !loaded,
            disableClear: !loaded,
            hideExtraActions: !loaded,
        });

        if (loadedFormContext) {
            loadedFormContext.setIsFormEditorOpen(loaded);
        }

        return () => {
            loadedFormContext?.setIsFormEditorOpen(false);
        };
    }, [loaded]);

    const onRemoveAttachment = async () => {
        if (publicFormRef.current) {
            await publicFormRef.current.close();
        }

        props.onRemoveAttachment();
    };

    React.useEffect(() => {
        if (containerRef.current) {
            setLoaded(false);
            window.CF.start(
                containerRef.current,
                {
                    draftId: props.draftId,
                    ns: props.ns,
                    showNavigationSidebar: false,
                    onRemoveAttachment: onRemoveAttachment,
                    onClearAttachment: props.onClearAttachment,
                    attachmentId: props.attachmentId,
                    attachmentPageActions: props.extraPageActions,
                    attachmentUnitItemInfoPaths: props.attachmentUnitItemInfoPaths,
                    setUnitItemExtraInfo: props.setUnitItemExtraInfo,
                    ...props.navigationSettings,
                },
                (...args) => {
                    setLoaded(true);

                    if (args[0]) {
                        throw args[0];
                    } else {
                        props.onRenderAttachmentForm(args[1]);
                        publicFormRef.current = args[1];
                    }
                }
            );
        }
    }, []);

    return (
        <div>
            {!loaded && (
                <HeaderWrapper>
                    <UnitCaption>{props.caption}</UnitCaption>
                    <Header>
                        <span>{props.description}</span>
                    </Header>
                </HeaderWrapper>
            )}
            <FormInFormContainer ref={containerRef} />
        </div>
    );
}
